import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router-dom';
import { ErrorBoundary } from '@shared/ui';
import { salesRoutes } from '@sales/app/routes/routeTree';
import { opsRoutes } from '@ops/app/routes/routeTree';
import { catalogRoutes } from '@catalog/app/routes/routeTree';
import { rfpRoutes } from '@rfp/app/routes/routeTree';
import { mapleRoutes } from '@maple/app/routes/routeTree';
import { ideasRoutes } from '@ideas/app/routes/routeTree';
import { marketingRoutes } from '@marketing/app/routes/routeTree';
import { financeRoutes } from '@finance/app/routes/routeTree';
import { academyRoutes } from '@academy/app/routes/routeTree';
import { portfolioRoutes } from '@portfolio/app/routes/routeTree';
import { shareRoutes } from '@share/app/routes/routeTree';
import { rxRoutes } from '@rx/app/routes/routeTree';
import { analyticsRoutes } from '@analytics/app/routes/routeTree';
import { ShellLayout } from '../ShellLayout';
import { LauncherPage } from '@pages/launcher';
import { AdminPage, AdminLayout, AuditLogPage, DemoPagesPage, BroadcastsPage, AiModelsPage, GeocodingPage } from '@pages/admin';
import { UserActivityPage } from '@pages/user-activity';

function RootLayout() {
  return (
    <ErrorBoundary>
      <ShellLayout>
        <Outlet />
      </ShellLayout>
    </ErrorBoundary>
  );
}

/**
 * RFP was renamed Proposal (display + route only, 2026-08-07): every old
 * `/rfp/...` link keeps working, subpath, query, and hash intact. The module
 * package, its role values, and `/api/rfp/*` are unchanged.
 */
function LegacyRfpRedirect() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`${pathname.replace(/^\/rfp/, '/proposal')}${search}${hash}`} replace />;
}

function LegacyClinicalRedirect() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`${pathname.replace(/^\/clinical/, '/analytics')}${search}${hash}`} replace />;
}

/**
 * Wired modules supply their own AppShell chrome, so we mount them directly
 * without the shell's `ShellLayout` wrapper. The launcher keeps the shell
 * chrome.
 */
export const router = createBrowserRouter([
  {
    path: 'sales/*',
    children: salesRoutes('/sales'),
  },
  {
    path: 'ops/*',
    children: opsRoutes('/ops'),
  },
  {
    path: 'catalog/*',
    children: catalogRoutes('/catalog'),
  },
  {
    path: 'proposal/*',
    children: rfpRoutes('/proposal'),
  },
  { path: 'rfp/*', element: <LegacyRfpRedirect /> },
  {
    path: 'maple/*',
    children: mapleRoutes('/maple'),
  },
  {
    path: 'ideas/*',
    children: ideasRoutes('/ideas'),
  },
  {
    path: 'marketing/*',
    children: marketingRoutes('/marketing'),
  },
  {
    path: 'finance/*',
    children: financeRoutes('/finance'),
  },
  {
    path: 'academy/*',
    children: academyRoutes('/academy'),
  },
  {
    path: 'portfolio/*',
    children: portfolioRoutes('/portfolio'),
  },
  {
    path: 'share/*',
    children: shareRoutes('/share'),
  },
  {
    path: 'rx/*',
    children: rxRoutes('/rx'),
  },
  {
    path: 'analytics/*',
    children: analyticsRoutes('/analytics'),
  },
  // Renamed Clinical -> Analytics 9/04, one day into local use: the redirect
  // costs three lines and spares anyone's open tab or bookmark.
  { path: 'clinical/*', element: <LegacyClinicalRedirect /> },
  {
    element: <RootLayout />,
    children: [
      { path: '/', element: <LauncherPage /> },
      // Guide retired into Academy Know (PLAN 11 step 3): redirects preserve
      // the old bookmarks; content management moved to /academy/authoring.
      { path: '/guide', element: <Navigate to="/academy/know" replace /> },
      {
        path: '/admin',
        element: <AdminLayout />,
        children: [
          // User Activity is the default admin tab, so it owns the index
          // route; Roles moved to /admin/roles and /admin/activity redirects
          // so existing bookmarks and links still land.
          { index: true, element: <UserActivityPage /> },
          { path: 'activity', element: <Navigate to="/admin" replace /> },
          { path: 'roles', element: <AdminPage /> },
          { path: 'audit', element: <AuditLogPage /> },
          { path: 'guide', element: <Navigate to="/academy/authoring" replace /> },
          { path: 'demos', element: <DemoPagesPage /> },
          { path: 'broadcasts', element: <BroadcastsPage /> },
          { path: 'ai-models', element: <AiModelsPage /> },
          { path: 'geocoding', element: <GeocodingPage /> },
        ],
      },
    ],
  },
]);
